import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import './forgot-password.css';
import HeaderSection from '../../../../components/layout/header';
import useNavigation from '../../../../shared/utils/useNavigation';
import { createForgotPasswordFormSchema } from '../../../../shared/validations/auth';
import { forgotPassword } from '../../../../shared/actions/authActions';
import { showError, showSuccess } from '../../../../shared/utils/swalHandler';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "../../../../shared/components/form";

const ModernForgotPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const form = useForm({
    resolver: zodResolver(createForgotPasswordFormSchema()),
    defaultValues: {
      email: ''
    },
    mode: "onChange",
  });

  const sendPasswordResetEmail = async (email) => {
    const result = await dispatch(forgotPassword(email));

    if (result.success) {
      return { success: true };
    }

    return {
      success: false,
      error: result.error || 'Failed to send reset email. Please try again.',
    };
  };

  const onSubmit = async (data) => {
    setIsLoading(true);

    try {
      const result = await sendPasswordResetEmail(data.email);

      if (result.success) {
        setIsSubmitted(true);
      } else {
        await showError('Send Failed', result.error);
      }
    } catch (error) {
      await showError('Send Failed', error.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmail = async () => {
    const email = form.getValues('email');
    if (!email) {
      setIsSubmitted(false);
      return;
    }

    setIsResending(true);

    try {
      const result = await sendPasswordResetEmail(email);

      if (result.success) {
        await showSuccess('Email Sent', 'We sent another password reset link to your email.');
      } else {
        await showError('Send Failed', result.error);
      }
    } catch (error) {
      await showError('Send Failed', error.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleGoToLogin = () => {
    navigate.toLogin();
  };

  return (
    <div className={`modern-forgot-password-screen`}>
      {/* Header */}
      <HeaderSection
        type="image"
      />

      <main className="modern-forgot-password-main">
        <div className="modern-forgot-password-bg-effects">
          <div className="modern-forgot-password-bg-gradient"></div>
          <div className="modern-forgot-password-bg-blur-1"></div>
          <div className="modern-forgot-password-bg-blur-2"></div>
        </div>

        <div className="modern-forgot-password-container">
          {!isSubmitted ? (
            <>
              <div className="modern-forgot-password-welcome">
                <div className="modern-forgot-password-icon">
                  <i className="fas fa-key"></i>
                </div>
                <h1 className="modern-forgot-password-welcome-title">Forgot Password?</h1>
                <p className="modern-forgot-password-welcome-subtitle">
                  No worries! Enter your email address and we'll send you a link to reset your password.
                </p>
                <div className="modern-forgot-password-welcome-divider"></div>
              </div>

              <form autoComplete="off">
                <input
                  type="text"
                  name="fakeuser"
                  style={{ display: 'none' }}
                  autoComplete="username"
                />

              
              </form>

              <div className="modern-forgot-password-form-container">
                <div className="modern-forgot-password-form-wrapper">
                  <Form {...form}>
                    <form className="modern-forgot-password-form" onSubmit={form.handleSubmit(onSubmit)}>
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem className="modern-forgot-password-input-group">
                            <FormLabel className="modern-forgot-password-label">Email Address</FormLabel>
                            <div className="modern-forgot-password-input-wrapper">
                              <div className="modern-forgot-password-input-icon">
                                <i className="far fa-envelope"></i>
                              </div>
                              <FormControl>
                                <input
                                type="text"
                                name="realEmail"
                                placeholder="Enter your email"
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="none"
                                spellCheck="false"
                                  className="modern-forgot-password-input"
                                  {...field}
                                />
                              </FormControl>
                              <div className="modern-forgot-password-input-shimmer"></div>
                            </div>
                          </FormItem>
                        )}
                      />


                      <button
                        type="submit"
                        className="modern-forgot-password-submit-btn"
                        disabled={isLoading || form.formState.isSubmitting || !form.formState.isValid}
                      >
                        {isLoading || form.formState.isSubmitting ? (
                          <span className="modern-forgot-password-btn-loading">
                            <i className="fas fa-spinner fa-spin"></i>
                            <span>Sending...</span>
                          </span>
                        ) : (
                          <span className="modern-forgot-password-btn-content">
                            <i className="fas fa-paper-plane"></i>
                            <span>Send Reset Link</span>
                          </span>
                        )}
                        <div className="modern-forgot-password-btn-shimmer"></div>
                      </button>
                    </form>
                  </Form>
                </div>
              </div>
            </>
          ) : (
            <div className="modern-forgot-password-success">
              <div className="modern-forgot-password-success-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <h1 className="modern-forgot-password-success-title">Check Your Email</h1>
              <p className="modern-forgot-password-success-subtitle">
                We've sent a password reset link to <strong>{form.watch('email')}</strong>
              </p>
              <div className="modern-forgot-password-success-message">
                <p>Didn't receive the email? Check your spam folder or try again.</p>
              </div>

              <div className="modern-forgot-password-success-actions">
                <button
                  type="button"
                  className="modern-forgot-password-resend-btn"
                  onClick={handleResendEmail}
                  disabled={isResending}
                >
                  {isResending ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-redo"></i>
                      <span>Send Again</span>
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className="modern-forgot-password-login-btn"
                  onClick={handleGoToLogin}
                  disabled={isResending}
                >
                  <i className="fas fa-arrow-left"></i>
                  <span>Back to Login</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ModernForgotPassword;
